import MaterialButton, { ButtonProps as MDButtonProps } from '@material-ui/core/Button';
import MaterialCircularProgress from '@material-ui/core/CircularProgress';
import React from 'react';
import { css, styled } from '~ui-kit/styled-components';
import { styledSpace, StyledSpaceProps, styledWidth, StyledWidthProps } from '~ui-kit/theme/styled-system';

export interface ButtonProps extends StyledWidthProps, StyledSpaceProps {
  id?: string;
  children: React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  shape?: 'rectangle' | 'pill' | 'square' | 'round';
  appearance?: 'filled' | 'outlined' | 'text' | 'none';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  type?: 'button' | 'submit';
  color?: 'primary' | 'secondary';
  textTransform?: React.CSSProperties['textTransform'];
  textDecoration?: React.CSSProperties['textDecoration'];
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  href?: string;
  target?: string;
  style?: React.CSSProperties;
}

const MaterialVariantMapper: Record<ButtonProps['appearance'], MDButtonProps['variant']> = {
  filled: 'contained',
  outlined: 'outlined',
  text: 'text', // same as none, but no padding/height
  none: 'text',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    isLoading,
    disabled,
    shape = 'rectangle',
    appearance = 'filled',
    size = 'lg',
    type = 'button',
    color = 'primary',
    textTransform,
    textDecoration,
    ...props
  }: ButtonProps,
  ref,
) {
  const extraProps = {
    ...props,
    $shape: shape,
    $textTransform: textTransform,
    $textDecoration: textDecoration,
    $appearance: appearance,
    $size: size,
    disabled: isLoading || disabled,
    variant: MaterialVariantMapper[appearance],
    type,
    color,
  };

  return (
    <StyledButton {...extraProps} ref={ref}>
      {isLoading ? <MaterialCircularProgress color="secondary" size="var(--icon-size)" /> : children}
    </StyledButton>
  );
});

type StyledProps = StyledSpaceProps &
  StyledWidthProps & {
    $shape?: ButtonProps['shape'];
    $size?: ButtonProps['size'];
    $textTransform?: ButtonProps['textTransform'];
    $textDecoration?: ButtonProps['textDecoration'];
    $appearance?: ButtonProps['appearance'];
  };

const StyledButton = styled(MaterialButton)<StyledProps>`
  && {
    font-weight: 500;
    min-height: var(--height);
    text-transform: ${({ $textTransform }) => $textTransform || 'uppercase'};
    text-decoration: ${({ $textDecoration }) => $textDecoration};
    border-radius: ${({ $shape }) =>
      ({
        pill: '100px',
        round: '100%',
      })[$shape] || '4px'};
    ${styledSpace}

    /* Prevent clicking on a child element for GA4 */
    * {
      pointer-events: none;
    }

    svg {
      width: var(--icon-size);
      height: var(--icon-size);
    }

    ${({ $size }) =>
      (
        ({
          sm: css`
            --height: 28px;
            --icon-size: 16px;
            min-width: 72px;
            line-height: 16px;
            padding: 4px 16px;
            font-size: 12px;
          `,
          md: css`
            --height: 38px;
            --icon-size: 22px;
            min-width: 96px;
            line-height: 18px;
            padding: 6px 18px;
            font-size: 13px;
          `,
          lg: css`
            --height: 48px;
            --icon-size: 28px;
            min-width: 112px;
            line-height: 20px;
            padding: 8px 24px;
            font-size: 14px;
          `,
          xl: css`
            --height: 58px;
            --icon-size: 36px;
            min-width: 112px;
            line-height: 22px;
            padding: 10px 30px;
            font-size: 15px;
          `,
        }) as Record<ButtonProps['size'], any>
      )[$size]}

    ${({ $appearance, $px, $pl, $pr }) =>
      $appearance === 'none' &&
      css`
        padding-left: ${$px || $pl || 0};
        padding-right: ${$px || $pr || 0};
      `}

    ${({ $appearance, $textDecoration, $textTransform }) =>
      $appearance === 'text' &&
      css`
        &,
        &:hover {
          padding: 0;
          text-decoration: ${$textDecoration || 'inherit'};
          text-transform: ${$textTransform || 'inherit'};
          --height: inherit;
          min-width: unset;
        }
      `}

    ${({ $width }) =>
      $width &&
      css`
        min-width: unset;
        flex-shrink: 0;
        ${styledWidth}
      `}

    ${({ $shape }) =>
      ($shape === 'square' || $shape === 'round') &&
      css`
        padding: 0;
        width: var(--height);
        min-width: var(--height);
        max-width: var(--height);
        height: var(--height);
        max-height: var(--height);
        line-height: var(--height);
        flex: 0 0 var(--height);
      `}

    &.MuiButton-outlinedSecondary:not(.Mui-disabled) {
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;
